/**
 * Pure helpers shared by the Keyword and Instant results tables
 * (DrugResultsTable, spec 2026-09-22 "one sortable, filterable table").
 *
 * No React here on purpose: sorting/filtering rules are unit-testable
 * without rendering, and the same rules apply to both modes.
 */

export interface DrugTableFields {
  brand_name: string | null;
  generic_name: string | null;
  manufacturer: string | null;
  region: string;
  approval_date?: string | null | undefined;
  approval_date_precision?: string | null | undefined;
  total_matches?: number | undefined;
}

export type DrugSortColumn =
  | 'brand'
  | 'generic'
  | 'manufacturer'
  | 'region'
  | 'approval_date'
  | 'matches';

export interface DrugSort {
  column: DrugSortColumn;
  direction: 'asc' | 'desc';
}

/** Columns that start sorted descending on their first click (spec §4.2). */
const DESC_FIRST: ReadonlySet<DrugSortColumn> = new Set(['approval_date', 'matches']);

/**
 * Raw YYYY-MM-DD value used for SORTING and text-filter matching — never
 * for display. Derived by SLICING the ISO string, never `new Date()`: a
 * date-only value parsed as UTC shifts back a day in US time zones (spec
 * §4.1), and slicing keeps the value lexicographically sortable regardless
 * of the row's `approval_date_precision`. Display formatting (which DOES
 * depend on precision) lives in `utils/approvalDate.ts`'s
 * `formatApprovalDate` — the two are deliberately separate. Returns null
 * (never `'—'`) so callers control the placeholder.
 */
export function sortableApprovalDate(value: string | null | undefined): string | null {
  return value ? value.slice(0, 10) : null;
}

/** Brand cell value: brand_name, falling back to generic_name (spec §4.1). */
export function brandLabel(row: DrugTableFields): string | null {
  return row.brand_name || row.generic_name || null;
}

/** Header click → next sort state: same column flips direction; a new
 *  column starts at its first-click direction (asc, except Approval Date
 *  and Matches which start desc). */
export function nextSort(current: DrugSort | null, column: DrugSortColumn): DrugSort {
  if (current && current.column === column) {
    return { column, direction: current.direction === 'asc' ? 'desc' : 'asc' };
  }
  return { column, direction: DESC_FIRST.has(column) ? 'desc' : 'asc' };
}

function isEmpty(v: string | number | null | undefined): v is null | undefined | '' {
  return v === null || v === undefined || v === '';
}

function sortValue<T extends DrugTableFields>(row: T, column: DrugSortColumn): string | number | null {
  switch (column) {
    case 'brand':
      return brandLabel(row);
    case 'generic':
      return row.generic_name || null;
    case 'manufacturer':
      return row.manufacturer || null;
    case 'region':
      return row.region || null;
    case 'approval_date':
      return sortableApprovalDate(row.approval_date);
    case 'matches':
      return row.total_matches ?? null;
  }
}

/** Compares two NON-EMPTY values. Direction-dependent — the caller applies
 *  the sort's sign to this result. Empty placement is handled separately in
 *  `sortDrugRows` (NOT sign-flipped: empties sort last in both directions). */
function compareNonEmpty(a: string | number, b: string | number, column: DrugSortColumn): number {
  if (column === 'matches') {
    return (a as number) - (b as number);
  }
  if (column === 'approval_date') {
    const as = a as string;
    const bs = b as string;
    return as < bs ? -1 : as > bs ? 1 : 0;
  }
  return (a as string).localeCompare(b as string, undefined, { sensitivity: 'base' });
}

/** Returns a SORTED COPY of `rows`; never mutates the input (it may be the
 *  React Query cache that Export/Compare read). Returns `rows` unchanged
 *  when `sort` is null (default/backend order). Stable: ties keep backend
 *  order (Array.prototype.sort is stable). */
export function sortDrugRows<T extends DrugTableFields>(rows: T[], sort: DrugSort | null): T[] {
  if (!sort) return rows;
  const { column, direction } = sort;
  const sign = direction === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => {
    const av = sortValue(a, column);
    const bv = sortValue(b, column);
    const aEmpty = isEmpty(av);
    const bEmpty = isEmpty(bv);
    // Empty values sort LAST in BOTH directions — decided before direction,
    // never multiplied by `sign` (that was a real bug: it put empties FIRST
    // on descending sorts).
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1;
    if (bEmpty) return -1;
    const cmp = compareNonEmpty(av, bv, column);
    return cmp === 0 ? 0 : cmp * sign;
  });
}

